import { Grid, Tooltip } from '@mui/material';
import { CustomFieldDefinition, CustomFieldValues } from '@nx-smartmonkey/shared/domain';
import { dateFormat, pastDateTime } from '@nx-smartmonkey/shared/helpers';
import { RoutalPalette, RoutalTooltip } from '@nx-smartmonkey/ui';

import More3Points from '../../../../assets/svg/More3Points';
import { TaskStatusChip } from '../../../../components/StatusChip';
import { customFieldToTableValue } from '../../../../components/TableColumnsButton';
import { CUSTOM_FIELD_COLUMN_ID_PREFIX } from '../../../../constants';
import { Driver } from '../../../../domain/driver/Driver';
import { Project } from '../../../../domain/project/Project';
import { Stop } from '../../../../domain/stop/Stop';
import { Task } from '../../../../domain/task/Task';
import Text from '../../../../react-components/Text';
import {
  AlignmentTypes,
  VirtualizedDataTableColumnProps,
} from '../../shared/virtualized-datatable/VirtualizedDataTable';
import { TasksTableColumn, TasksTableColumnKeys } from '../context/tableColumns/tasksTableColumns';
import { PlanDriversActionsMenu } from '../menus/DriversActionsMenu';

type IColumnsResponse = Record<string, any>[];

type IDriversGetColumns = {
  activeProject: Project;
  showRootParams?: boolean;
  planStops: Array<Stop>;
  planDrivers: Array<Driver>;
  tableColumns: Array<TasksTableColumn>;
};

export const getTasksColumns = ({
  activeProject,
  showRootParams = false,
  tableColumns,
  planStops,
  planDrivers,
}: IDriversGetColumns): IColumnsResponse => {
  const columns: Array<VirtualizedDataTableColumnProps> = [];

  const custom_fields = [
    ...(activeProject.custom_fields?.task ?? []).filter((customFieldDefinition) =>
      activeProject.planner.taskHasCustomField(customFieldDefinition.custom_id)
    ),
    ...(activeProject.custom_fields?.service_report_task_canceled ?? []),
    ...(activeProject.custom_fields?.service_report_task_completed ?? []),
  ];

  const taskCustomFieldDefinitionsMap: Record<string, CustomFieldDefinition> = custom_fields.reduce(
    (amount, customFieldDefinition) => {
      return {
        ...amount,
        [`${CUSTOM_FIELD_COLUMN_ID_PREFIX}${customFieldDefinition.custom_id}`]: customFieldDefinition,
      };
    },
    {}
  );

  tableColumns.forEach(({ displayable, identifier, sortable }: TasksTableColumn) => {
    if (displayable) {
      switch (identifier) {
        case `driver_id`:
          columns.push({
            width: 150,
            accessorName: `driver_id`,
            sortable,
            name: (
              <Text
                style={{ color: RoutalPalette.primary20 }}
                isBold
                intlId="planoverview.route.table.header.driver_id"
              />
            ),
            formatter: ({ doc: task }) => {
              const driver = planDrivers.find((driver) => driver.id === task.driver_id);
              return (
                <Grid container direction="row" alignItems="center">
                  {driver && driver.label && <Text variant="body">{driver.label}</Text>}
                </Grid>
              );
            },
          });
          break;
        case `stop_id`:
          columns.push({
            width: 150,
            accessorName: `stop`,
            sortable,
            name: (
              <Text
                style={{ color: RoutalPalette.primary20 }}
                isBold
                intlId="planoverview.route.table.header.stop_id"
              />
            ),
            formatter: ({ doc: task }) => {
              const stop = planStops.find((stop) => stop.id === task.stop_id);
              return (
                <Grid container direction="row" alignItems="center">
                  {stop && stop.label && <Text variant="body">{stop.label}</Text>}
                </Grid>
              );
            },
          });
          break;
        case `stop_order`:
          columns.push({
            width: 110,
            accessorName: `stop_order`,
            sortable,
            name: (
              <Text
                style={{ color: RoutalPalette.primary20 }}
                isBold
                intlId="planoverview.route.table.header.stop_order"
              />
            ),
            formatter: ({ doc: task }) => {
              const stop = planStops.find((stop) => stop.id === task.stop_id);
              return (
                <Grid container direction="row" alignItems="center">
                  {stop && stop.order && <Text variant="body">{stop.order}</Text>}
                </Grid>
              );
            },
          });
          break;
        case `label`:
          columns.push({
            width: 250,
            accessorName: `label`,
            sortable,
            name: (
              <Text style={{ color: RoutalPalette.primary20 }} isBold intlId="planoverview.route.table.header.label" />
            ),
            formatter: ({ doc: task }) => (
              <Grid container direction="row" alignItems="center">
                {task.label && task.label !== `` ? (
                  <Tooltip title={task.label}>
                    <Text variant="body">
                      {task.label.length > 30 ? `${task.label.substring(0, 30)}...` : `${task.label}`}
                    </Text>
                  </Tooltip>
                ) : (
                  <Text variant="body" style={{ color: RoutalPalette.secondary00 }} intlId="no_name" />
                )}
              </Grid>
            ),
          });
          break;
        case `status`:
          columns.push({
            width: 135,
            accessorName: `status`,
            sortable,
            name: (
              <Text style={{ color: RoutalPalette.primary20 }} isBold intlId="planoverview.route.table.header.status" />
            ),
            formatter: ({ value: status }) =>
              status && (
                <Grid container alignItems="center" justifyContent="flex-start" width={135}>
                  <TaskStatusChip status={status} />
                </Grid>
              ),
          });
          break;
        case `comments`:
          if (activeProject.planner.taskFieldsHasSpecificField(`comments`)) {
            columns.push({
              width: 110,
              accessorName: identifier,
              align: `center` as AlignmentTypes,
              sortable,
              name: (
                <Text
                  style={{ color: RoutalPalette.primary20 }}
                  isBold
                  intlId={`planoverview.route.table.header.comments`}
                />
              ),
              formatter: ({ value: comments }) =>
                comments && (
                  <Tooltip title={comments}>
                    <Text variant="body" style={{ maxWidth: `110px` }}>
                      {comments}
                    </Text>
                  </Tooltip>
                ),
            });
          }
          break;
        case `barcode`:
          if (activeProject.planner.taskFieldsHasSpecificField(`barcode`)) {
            columns.push({
              width: 160,
              accessorName: identifier,
              align: `center` as AlignmentTypes,
              sortable,
              name: (
                <Text
                  style={{ color: RoutalPalette.primary20 }}
                  isBold
                  intlId={`planoverview.route.table.header.barcode`}
                />
              ),
              formatter: ({ value: barcode }) =>
                barcode && (
                  <RoutalTooltip title={barcode} contentStyles={{ maxWidth: `none` }}>
                    <Text variant="body" style={{ maxWidth: `160px` }}>
                      {barcode}
                    </Text>
                  </RoutalTooltip>
                ),
            });
          }
          break;
        case `created_at`:
          break;
        default: {
          if (
            (identifier as TasksTableColumnKeys).includes(CUSTOM_FIELD_COLUMN_ID_PREFIX) &&
            identifier in taskCustomFieldDefinitionsMap
          ) {
            const customFieldDefinition = taskCustomFieldDefinitionsMap[identifier];
            columns.push({
              width: 160,
              accessorName: identifier,
              sortable,
              align: `center` as AlignmentTypes,
              name: (
                <Text style={{ color: RoutalPalette.primary20 }} isBold>
                  {customFieldDefinition.label}
                </Text>
              ),
              formatter: ({ doc }: { doc: Task }) => {
                if (!doc?.custom_fields) {
                  return null;
                }
                const driverCustomFields = doc.custom_fields.value;
                const customField = driverCustomFields[customFieldDefinition.custom_id] as CustomFieldValues;

                return customFieldToTableValue({
                  doc,
                  customField,
                  customFieldDefinition,
                });
              },
            });
          }
          break;
        }
      }
    }
  });

  return [
    ...columns,
    ...(tableColumns.find(({ displayable, identifier }) => identifier === `created_at` && displayable)
      ? [
          {
            width: 130,
            accessorName: `created_at`,
            align: `center` as AlignmentTypes,
            sortable: true,
            name: (
              <Text
                style={{ color: RoutalPalette.primary20 }}
                isBold
                intlId="planoverview.route.table.header.created_at"
              />
            ),
            formatter: ({ value: createdAt }) => (
              <RoutalTooltip title={dateFormat(createdAt)}>
                <Text variant="body">{pastDateTime(createdAt)}</Text>
              </RoutalTooltip>
            ),
          } as VirtualizedDataTableColumnProps,
        ]
      : []),
    {
      accessorName: `id`,
      align: `right`,
      width: 100,
      flex: 1,
      formatter: ({ doc: driver, rowHover }) => (
        <Grid container justifyContent="flex-end">
          {rowHover && (
            <PlanDriversActionsMenu
              type="popover"
              planStops={planStops}
              planDrivers={planDrivers}
              activeProject={activeProject}
              drivers={[driver]}
              showViewDetails={false}
              popoverProps={{
                hideArrow: true,
                content: <More3Points />,
                style: { height: `18px`, color: RoutalPalette.secondary60, padding: 0, margin: 0, border: `unset` },
              }}
            />
          )}
        </Grid>
      ),
    },
  ] as Array<VirtualizedDataTableColumnProps>;
};
